import { useEffect, useRef, useState } from "react";
import { Eye, Pencil, Copy, Check, Trash2, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useInviteLinks,
  useCreateInviteLink,
  useDeleteInviteLink,
} from "@/hooks/use-invite-links";
import type { InviteLink, LinkType } from "@/types/api";

interface ShareDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShareDialog({
  projectId,
  open,
  onOpenChange,
}: ShareDialogProps) {
  const { data: links, isLoading, error } = useInviteLinks(projectId, open);
  const createLink = useCreateInviteLink(projectId);
  const deleteLink = useDeleteInviteLink(projectId);
  const [copied, setCopied] = useState<LinkType | null>(null);
  const [copyError, setCopyError] = useState<LinkType | null>(null);
  // Per-linkType local state so a failure on one section persists even when
  // the user triggers the *other* section — the mutation itself is shared
  // across both sections, so we can't rely on `mutation.isError` for scoping.
  const [createError, setCreateError] = useState<Record<LinkType, boolean>>({
    view: false,
    edit: false,
  });
  const [deleteError, setDeleteError] = useState<Record<LinkType, boolean>>({
    view: false,
    edit: false,
  });
  const copyErrorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyErrorTimeoutRef.current) {
        clearTimeout(copyErrorTimeoutRef.current);
      }
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  const viewLink = links?.find((l) => l.link_type === "view" && l.is_active);
  const editLink = links?.find((l) => l.link_type === "edit" && l.is_active);

  const handleCopy = async (linkType: LinkType, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      if (copyErrorTimeoutRef.current) {
        clearTimeout(copyErrorTimeoutRef.current);
        copyErrorTimeoutRef.current = null;
      }
      setCopyError((prev) => (prev === linkType ? null : prev));
      setCopied(linkType);
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
      copiedTimeoutRef.current = setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard unavailable (insecure context, denied permission, etc.).
      // Do NOT set copied — show inline error instead. URL remains in the
      // read-only input so the user can long-press / select + copy manually.
      setCopyError(linkType);
      if (copyErrorTimeoutRef.current) {
        clearTimeout(copyErrorTimeoutRef.current);
      }
      copyErrorTimeoutRef.current = setTimeout(
        () => setCopyError(null),
        4000,
      );
    }
  };

  const handleGenerate = (linkType: LinkType) => {
    // Clear the error for just this section before retrying; a view-only
    // failure must persist when the user interacts with edit-access.
    setCreateError((prev) => ({ ...prev, [linkType]: false }));
    createLink.mutate(linkType, {
      onError: () => {
        setCreateError((prev) => ({ ...prev, [linkType]: true }));
      },
    });
  };

  const handleDelete = (linkType: LinkType) => {
    setDeleteError((prev) => ({ ...prev, [linkType]: false }));
    deleteLink.mutate(linkType, {
      onError: () => {
        setDeleteError((prev) => ({ ...prev, [linkType]: true }));
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Share Project</DialogTitle>
          <DialogDescription>
            Create share links to invite others to this project.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
            Failed to fetch links
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <LinkSection
              icon={<Eye className="h-5 w-5" />}
              title="View-only Link"
              description="Anyone with this link can view the project"
              link={viewLink}
              linkType="view"
              isGenerating={createLink.isPending && createLink.variables === "view"}
              isDeleting={deleteLink.isPending && deleteLink.variables === "view"}
              isCopied={copied === "view"}
              hasCopyError={copyError === "view"}
              hasCreateError={createError.view}
              hasDeleteError={deleteError.view}
              onGenerate={() => handleGenerate("view")}
              onDelete={() => handleDelete("view")}
              onCopy={handleCopy}
            />

            <LinkSection
              icon={<Pencil className="h-5 w-5" />}
              title="Edit Access Link"
              description="Anyone with this link can join as an editor"
              link={editLink}
              linkType="edit"
              isGenerating={createLink.isPending && createLink.variables === "edit"}
              isDeleting={deleteLink.isPending && deleteLink.variables === "edit"}
              isCopied={copied === "edit"}
              hasCopyError={copyError === "edit"}
              hasCreateError={createError.edit}
              hasDeleteError={deleteError.edit}
              onGenerate={() => handleGenerate("edit")}
              onDelete={() => handleDelete("edit")}
              onCopy={handleCopy}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface LinkSectionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  link: InviteLink | undefined;
  linkType: LinkType;
  isGenerating: boolean;
  isDeleting: boolean;
  isCopied: boolean;
  hasCopyError: boolean;
  hasCreateError: boolean;
  hasDeleteError: boolean;
  onGenerate: () => void;
  onDelete: () => void;
  onCopy: (linkType: LinkType, url: string) => void;
}

function LinkSection({
  icon,
  title,
  description,
  link,
  linkType,
  isGenerating,
  isDeleting,
  isCopied,
  hasCopyError,
  hasCreateError,
  hasDeleteError,
  onGenerate,
  onDelete,
  onCopy,
}: LinkSectionProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <div className="text-muted-foreground mt-0.5">{icon}</div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-sm">{title}</h3>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>

      {link ? (
        <div className="flex items-center gap-2">
          <Input readOnly value={link.share_url} className="text-xs" />
          <Button
            variant="outline"
            size="icon"
            onClick={() => onCopy(linkType, link.share_url)}
          >
            {isCopied ? (
              <Check className="h-4 w-4 text-green-600" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={onDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 text-destructive" />
            )}
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          className="w-full"
          onClick={onGenerate}
          disabled={isGenerating}
          loading={isGenerating}
        >
          Generate Link
        </Button>
      )}

      {hasCopyError && (
        <p className="text-xs text-destructive">
          Copy failed — long-press the URL to copy manually
        </p>
      )}
      {hasCreateError && (
        <p className="text-xs text-destructive">
          Failed to create link. Try again.
        </p>
      )}
      {hasDeleteError && (
        <p className="text-xs text-destructive">
          Failed to delete link. Try again.
        </p>
      )}
    </div>
  );
}
